/* Solicitudes de mantenimiento correctivo a GafCORE: Solicitudes > GafCORE > Mantenimientos.
 *
 * api-central es un pasamanos hacia EMSYS, que guarda la solicitud y habla con
 * GafCORE (donde la atiende GAF). Mismo esquema que services/traslados.ts.
 *
 * Pide cualquier cargo menos los operarios. La app solo oculta la opcion; EMSYS
 * lo decide (permiso) y lo vuelve a controlar al crear.
 */
import api from "src/services/api";
import { LugarGafcore } from "src/services/traslados";

const BASE = "mantenimientos";

interface ApiOk<T> {
  ok: boolean;
  data: T;
}

export interface CatalogoMantenimientos {
  situaciones: { valor: string; etiqueta: string; color: string }[];
  max_foto_bytes: number;
}

/** Del equipo se muestra solo codigo y nombre; el lugar precarga "¿Dónde está?". */
export interface ActivoMantenimiento {
  codigo_g: string;
  cod_fracttal?: string | null;
  nombre: string;
  ubicacion_codigo?: string | null;
  ubicacion_nombre?: string | null;
}

/** PS para buscar el equipo. codigo es el del lugar en GafCORE (el objeto del PS). */
export interface PuntoServicioMantenimiento {
  codigo: string;
  nombre: string;
  equipos?: number | null;
  /** false: el PS no existe en GafCORE (no tiene equipos que listar). null: no se pudo saber. */
  en_gafcore?: boolean | null;
}

/** propios = los del fiscal o los de sus fiscales (jefe); todos = se busca cualquiera. */
export interface PuntosServicioMantenimiento {
  alcance: "propios" | "todos";
  puntos: PuntoServicioMantenimiento[];
}

export interface SolicitudMantenimientoResumen {
  id: number;
  referencia: string;
  gaf_codigo?: string | null;
  situacion: string;
  situacion_label: string;
  situacion_color: string;
  cerrado: boolean;
  codigo_g: string;
  cod_fracttal?: string | null;
  activo_nombre?: string | null;
  paro_equipo: boolean;
  lugar?: string | null;
  creado_en?: string | null;
}

export interface SolicitudMantenimientoDetalle extends SolicitudMantenimientoResumen {
  descripcion: string;
  fecha_incidente?: string | null;
  foto?: string | null;
  envio_estado: string;
  envio_error?: string | null;
  motivo_cierre?: string | null;
  duplicada_de?: string | null;
  ubicacion_coincide?: boolean | null;
  ubicacion_segun_gafcore?: string | null;
  otras_abiertas: string[];
  fuera_del_patrimonio: boolean;
  hitos: { etiqueta: string; fecha: string | null }[];
  sin_conexion: boolean;
}

export const MantenimientosAPI = {
  permiso: () => api.get<ApiOk<{ puede_solicitar: boolean; mensaje?: string | null }>>(`${BASE}/permiso`),
  catalogo: () => api.get<ApiOk<CatalogoMantenimientos>>(`${BASE}/catalogo`),
  lugares: (q: string) => api.get<ApiOk<LugarGafcore[]>>(`${BASE}/lugares`, { params: { q } }),
  // Por texto (q) o los equipos de un PS (ubicacion = su codigo de lugar).
  activos: (params: { q?: string; ubicacion?: string }) =>
    api.get<ApiOk<{ activos: ActivoMantenimiento[]; hay_mas: boolean }>>(`${BASE}/activos`, { params }),
  puntosServicio: (q = "") => api.get<ApiOk<PuntosServicioMantenimiento>>(`${BASE}/puntos-servicio`, { params: { q } }),
  mis: () => api.get<ApiOk<SolicitudMantenimientoResumen[]>>(`${BASE}/solicitudes`),
  // El alta espera la respuesta de GafCORE y puede llevar una foto: mas margen.
  crear: (payload: Record<string, any>) =>
    api.post<ApiOk<{ id: number; enviada: boolean; info: string }>>(`${BASE}/solicitudes`, payload, {
      timeout: 90000,
    }),
  detalle: (id: number) => api.get<ApiOk<SolicitudMantenimientoDetalle>>(`${BASE}/solicitudes/${id}`),
};

/**
 * Achica una foto en el celular (JPEG, lado mayor 1600 px) hasta que entre en
 * el limite de GafCORE (~1 MB). Devuelve un data URL.
 */
export const achicarFoto = (archivo: File, maxBytes: number): Promise<string> =>
  new Promise((ok, mal) => {
    const lector = new FileReader();
    lector.onerror = () => mal(new Error("No se pudo leer la foto."));
    lector.onload = () => {
      const img = new Image();
      img.onerror = () => mal(new Error("El archivo no es una imagen válida."));
      img.onload = () => {
        let lado = 1600;
        let calidad = 0.85;
        for (let i = 0; i < 8; i++) {
          const escala = Math.min(1, lado / Math.max(img.width, img.height));
          const lienzo = document.createElement("canvas");
          lienzo.width = Math.round(img.width * escala);
          lienzo.height = Math.round(img.height * escala);
          lienzo.getContext("2d")?.drawImage(img, 0, 0, lienzo.width, lienzo.height);
          const url = lienzo.toDataURL("image/jpeg", calidad);
          const bytes = Math.ceil(((url.length - url.indexOf(",") - 1) * 3) / 4);
          if (bytes <= maxBytes * 0.95) return ok(url);
          if (calidad > 0.55) calidad -= 0.1;
          else lado = Math.round(lado * 0.8);
        }
        mal(new Error("La foto es muy pesada. Probá con otra."));
      };
      img.src = String(lector.result);
    };
    lector.readAsDataURL(archivo);
  });
